import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  listAllCommunitySubmissions,
  updateCommunitySubmission,
  deleteCommunitySubmission,
  type CommunitySubmission,
  type CommunitySubmissionStatus,
} from "@/lib/community-submissions";
import {
  SETTING_COMMUNITY_MAX_IMAGES,
  SETTING_WATERMARK_URL,
  SETTING_WATERMARK_X,
  SETTING_WATERMARK_Y,
  SETTING_WATERMARK_SCALE,
  SETTING_WATERMARK_OPACITY,
  getSettingInt,
  getSettingText,
  setSettingInt,
  setSettingText,
} from "@/lib/app-settings";
import { toWebP, safeContentType } from "@/lib/image-convert";

export const Route = createFileRoute("/admin/community")({
  component: AdminCommunityPage,
});

const TABS: { id: CommunitySubmissionStatus | "all"; label: string }[] = [
  { id: "pending", label: "Offen" },
  { id: "approved", label: "Freigegeben" },
  { id: "rejected", label: "Abgelehnt" },
  { id: "all", label: "Alle" },
];

function AdminCommunityPage() {
  const [tab, setTab] = useState<CommunitySubmissionStatus | "all">("pending");
  const [items, setItems] = useState<CommunitySubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maxImages, setMaxImages] = useState<number>(1);
  const [maxImagesDraft, setMaxImagesDraft] = useState<string>("1");
  const [savingMax, setSavingMax] = useState(false);
  const [maxMsg, setMaxMsg] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<CommunitySubmission | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CommunitySubmission | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    getSettingInt(SETTING_COMMUNITY_MAX_IMAGES, 1).then((v) => {
      setMaxImages(v);
      setMaxImagesDraft(String(v));
    });
  }, []);

  async function saveMaxImages() {
    const n = parseInt(maxImagesDraft, 10);
    if (!Number.isFinite(n) || n < 1 || n > 50) {
      setMaxMsg("Bitte einen Wert zwischen 1 und 50 angeben.");
      return;
    }
    setSavingMax(true); setMaxMsg(null);
    try {
      await setSettingInt(SETTING_COMMUNITY_MAX_IMAGES, n);
      setMaxImages(n);
      setMaxMsg("Gespeichert.");
    } catch (e) {
      setMaxMsg(e instanceof Error ? e.message : "Fehler beim Speichern.");
    } finally {
      setSavingMax(false);
    }
  }

  async function load() {
    setLoading(true); setError(null);
    try { setItems(await listAllCommunitySubmissions(tab)); }
    catch (e) { setError(e instanceof Error ? e.message : "Fehler beim Laden."); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  async function handleStatus(id: string, status: CommunitySubmissionStatus) {
    try {
      await updateCommunitySubmission(id, status, null);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : "Fehler."); }
  }

  function openReject(item: CommunitySubmission) {
    setRejectTarget(item);
    setRejectNote(item.admin_note ?? "");
  }

  async function confirmReject() {
    if (!rejectTarget) return;
    setRejectBusy(true);
    try {
      await updateCommunitySubmission(rejectTarget.id, "rejected", rejectNote.trim() || null);
      setRejectTarget(null);
      setRejectNote("");
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Fehler.");
    } finally {
      setRejectBusy(false);
    }
  }

  async function handleDelete(id: string) {
    const item = items.find((i) => i.id === id) ?? null;
    setDeleteTarget(item);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteCommunitySubmission(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Fehler.");
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div>
      <div className="eyebrow text-muted-foreground">Community Selected</div>
      <h1 className="serif text-4xl md:text-5xl mt-3">Einreichungen prüfen</h1>

      <div className="mt-8 border border-border p-5 grid gap-3 md:flex md:items-end md:gap-6">
        <div>
          <div className="eyebrow text-muted-foreground">Einstellung</div>
          <div className="serif text-lg mt-1">Maximale Bilder pro Einreichung</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">
            Aktuell: <strong>{maxImages}</strong>. Legt fest, wie viele Bilder Fotografen pro Einreichung im Selected-Bereich hochladen dürfen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={50}
            value={maxImagesDraft}
            onChange={(e) => setMaxImagesDraft(e.target.value)}
            className="w-24 border border-input bg-background px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={saveMaxImages}
            disabled={savingMax}
            className="eyebrow border border-foreground bg-foreground text-background px-4 py-2 disabled:opacity-50"
          >{savingMax ? "Speichert …" : "Speichern"}</button>
        </div>
        {maxMsg && <div className="text-xs text-muted-foreground">{maxMsg}</div>}
      </div>

      <WatermarkEditor />

      <div className="flex gap-4 mt-8 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`eyebrow pb-3 ${tab === t.id ? "border-b-2 border-foreground text-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >{t.label}</button>
        ))}
      </div>

      {loading ? (
        <p className="mt-10 text-sm text-muted-foreground">Lädt …</p>
      ) : error ? (
        <p className="mt-10 text-sm text-destructive">{error}</p>
      ) : items.length === 0 ? (
        <p className="mt-10 text-sm text-muted-foreground">Keine Einreichungen.</p>
      ) : (
        <div className="mt-10 grid gap-6">
          {items.map((s) => (
            <div key={s.id} className="border border-border p-4 grid md:grid-cols-12 gap-6">
              <div className="md:col-span-4">
                <a href={s.image_url} target="_blank" rel="noreferrer">
                  <img src={s.image_url} alt={s.caption ?? s.submitter_name} className="w-full aspect-[4/5] object-cover" />
                </a>
              </div>
              <div className="md:col-span-8 text-sm flex flex-col gap-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="serif text-xl">{s.submitter_name}</div>
                    <a href={`mailto:${s.submitter_email}`} className="text-muted-foreground hover:text-foreground">{s.submitter_email}</a>
                  </div>
                  <StatusBadge status={s.status} />
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {s.region && <div><span className="eyebrow">Region: </span>{s.region}</div>}
                  {s.theme && <div><span className="eyebrow">Thema: </span>{s.theme}</div>}
                  {s.instagram_handle && <div><span className="eyebrow">IG: </span>{s.instagram_handle}</div>}
                  {s.website_url && <div className="truncate"><span className="eyebrow">Web: </span><a href={s.website_url} target="_blank" rel="noreferrer" className="hover:text-foreground">{s.website_url}</a></div>}
                  <div><span className="eyebrow">Eingereicht: </span>{new Date(s.created_at).toLocaleDateString("de-DE")}</div>
                </div>
                {s.caption && <p className="italic">"{s.caption}"</p>}
                {s.bio && <p className="text-muted-foreground">{s.bio}</p>}
                {s.admin_note && <p className="text-xs text-muted-foreground"><span className="eyebrow">Notiz: </span>{s.admin_note}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  {s.status !== "approved" && (
                    <button onClick={() => handleStatus(s.id, "approved")} className="eyebrow border border-foreground bg-foreground text-background px-4 py-1.5">Freigeben</button>
                  )}
                  {s.status !== "rejected" && (
                    <button onClick={() => openReject(s)} className="eyebrow border border-foreground px-4 py-1.5">Ablehnen</button>
                  )}
                  {s.status !== "pending" && (
                    <button onClick={() => handleStatus(s.id, "pending")} className="eyebrow border border-border text-muted-foreground px-4 py-1.5">Zurück zu „Offen"</button>
                  )}
                  <button onClick={() => handleDelete(s.id)} className="eyebrow border border-destructive text-destructive px-4 py-1.5 ml-auto">Löschen</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 bg-foreground/40 flex items-end md:items-center justify-center p-4" onClick={() => !rejectBusy && setRejectTarget(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border w-full max-w-lg p-6 md:p-8">
            <div className="eyebrow text-muted-foreground">Einreichung ablehnen</div>
            <h2 className="serif text-2xl mt-2">{rejectTarget.submitter_name}</h2>
            <p className="text-sm text-muted-foreground mt-3">
              Dieser Text wird als Absage-E-Mail an <strong>{rejectTarget.submitter_email}</strong> gesendet
              und intern als Notiz gespeichert. Lass das Feld leer, um eine neutrale Standard-Absage zu senden.
            </p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder="Persönliche Nachricht an die Person (optional) …"
              className="mt-4 w-full border border-border bg-transparent p-3 text-sm"
            />
            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <button type="button" disabled={rejectBusy} onClick={() => setRejectTarget(null)} className="eyebrow border border-border px-4 py-2 disabled:opacity-50">Abbrechen</button>
              <button type="button" disabled={rejectBusy} onClick={confirmReject} className="eyebrow border border-destructive text-destructive px-4 py-2 disabled:opacity-50">{rejectBusy ? "Sendet …" : "Ablehnen & senden"}</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 bg-foreground/40 flex items-end md:items-center justify-center p-4"
          onClick={() => !deleteBusy && setDeleteTarget(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border w-full max-w-md p-6 md:p-8">
            <div className="eyebrow text-muted-foreground">Löschen bestätigen</div>
            <h2 className="serif text-2xl mt-2">{deleteTarget.submitter_name}</h2>
            <p className="text-sm text-muted-foreground mt-3">
              Diese Einreichung wird endgültig entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => setDeleteTarget(null)}
                className="eyebrow border border-border px-4 py-2 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={deleteBusy}
                onClick={confirmDelete}
                className="eyebrow bg-destructive text-destructive-foreground px-4 py-2 disabled:opacity-50"
              >
                {deleteBusy ? "Lösche …" : "Endgültig löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: CommunitySubmissionStatus }) {
  const map: Record<CommunitySubmissionStatus, string> = {
    pending: "bg-muted text-foreground",
    approved: "bg-foreground text-background",
    rejected: "border border-destructive text-destructive",
  };
  const label = { pending: "Offen", approved: "Freigegeben", rejected: "Abgelehnt" }[status];
  return <span className={`eyebrow px-2 py-1 text-xs ${map[status]}`}>{label}</span>;
}

const WATERMARK_BUCKET = "community-selected";
const SAMPLE_IMAGE_URL =
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1600&q=80";

function WatermarkEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [x, setX] = useState(50);
  const [y, setY] = useState(92);
  const [scale, setScale] = useState(25);
  const [opacity, setOpacity] = useState(94);
  const [sampleUrl, setSampleUrl] = useState(SAMPLE_IMAGE_URL);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);

  useEffect(() => {
    Promise.all([
      getSettingText(SETTING_WATERMARK_URL, null),
      getSettingInt(SETTING_WATERMARK_X, 50),
      getSettingInt(SETTING_WATERMARK_Y, 92),
      getSettingInt(SETTING_WATERMARK_SCALE, 25),
      getSettingInt(SETTING_WATERMARK_OPACITY, 94),
    ]).then(([u, xv, yv, sv, ov]) => {
      setUrl(u);
      setX(xv);
      setY(yv);
      setScale(sv);
      setOpacity(ov);
      setLoading(false);
    });
    // optional: nutze neuestes freigegebenes Bild als Vorschau-Hintergrund
    supabase
      .from("community_submissions")
      .select("image_url")
      .eq("status", "approved")
      .order("reviewed_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.image_url) setSampleUrl(data.image_url);
      });
  }, []);

  async function handleUpload(file: File) {
    if (!file.type.startsWith("image/")) {
      setMsg("Bitte ein Bild auswählen (PNG mit Transparenz empfohlen).");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setMsg("Datei zu groß (max. 4 MB).");
      return;
    }
    setUploading(true);
    setMsg(null);
    try {
      // Zentrales WebP-Setup (identisch zu allen anderen Upload-Pfaden).
      const optimized = await toWebP(file);
      const ext = (optimized.name.split(".").pop() || "png").toLowerCase();
      const path = `_watermark/wm-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(WATERMARK_BUCKET)
        .upload(path, optimized, { cacheControl: "31536000", contentType: safeContentType(optimized), upsert: true });
      if (upErr) throw new Error(upErr.message);
      const { data: pub } = supabase.storage.from(WATERMARK_BUCKET).getPublicUrl(path);
      setUrl(pub.publicUrl);
      setMsg("Wasserzeichen hochgeladen. Position anpassen und speichern.");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setMsg(null);
    try {
      await Promise.all([
        setSettingText(SETTING_WATERMARK_URL, url),
        setSettingInt(SETTING_WATERMARK_X, Math.round(x)),
        setSettingInt(SETTING_WATERMARK_Y, Math.round(y)),
        setSettingInt(SETTING_WATERMARK_SCALE, Math.round(scale)),
        setSettingInt(SETTING_WATERMARK_OPACITY, Math.round(opacity)),
      ]);
      setMsg("Gespeichert. Neue Einreichungen verwenden dieses Wasserzeichen.");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (!confirm('Wasserzeichen-Bild entfernen? Neue Einreichungen verwenden dann wieder den Textfallback „OM Selected".')) return;
    setSaving(true);
    setMsg(null);
    try {
      await setSettingText(SETTING_WATERMARK_URL, null);
      setUrl(null);
      setMsg("Wasserzeichen-Bild entfernt.");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Fehler.");
    } finally {
      setSaving(false);
    }
  }

  function updatePosFromEvent(e: React.MouseEvent | React.TouchEvent) {
    const el = previewRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const point = "touches" in e ? e.touches[0] : (e as React.MouseEvent);
    const px = ((point.clientX - rect.left) / rect.width) * 100;
    const py = ((point.clientY - rect.top) / rect.height) * 100;
    setX(Math.max(0, Math.min(100, px)));
    setY(Math.max(0, Math.min(100, py)));
  }

  return (
    <div className="mt-6 border border-border p-5">
      <div className="eyebrow text-muted-foreground">Einstellung</div>
      <div className="serif text-lg mt-1">Wasserzeichen für Community-Einreichungen</div>
      <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
        Lade ein eigenes Wasserzeichen-Bild hoch (PNG mit transparentem Hintergrund empfohlen) und positioniere es per Klick/Drag auf der Vorschau.
        Wird auf jede neue Einreichung im Selected-Bereich eingebrannt. Ohne Bild wird der Text „OM Selected" als Fallback verwendet.
      </p>

      {loading ? (
        <p className="text-sm text-muted-foreground mt-4">Lädt …</p>
      ) : (
        <div className="mt-5 grid md:grid-cols-2 gap-6">
          <div>
            <div
              ref={previewRef}
              onMouseDown={(e) => { dragging.current = true; updatePosFromEvent(e); }}
              onMouseMove={(e) => { if (dragging.current) updatePosFromEvent(e); }}
              onMouseUp={() => { dragging.current = false; }}
              onMouseLeave={() => { dragging.current = false; }}
              onTouchStart={(e) => { dragging.current = true; updatePosFromEvent(e); }}
              onTouchMove={(e) => { if (dragging.current) updatePosFromEvent(e); }}
              onTouchEnd={() => { dragging.current = false; }}
              className="relative w-full aspect-[4/5] bg-muted overflow-hidden cursor-crosshair select-none"
              style={{
                backgroundImage: `url(${sampleUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {url ? (
                <img
                  src={url}
                  alt="Wasserzeichen-Vorschau"
                  draggable={false}
                  style={{
                    position: "absolute",
                    left: `${x}%`,
                    top: `${y}%`,
                    width: `${scale}%`,
                    transform: "translate(-50%, -50%)",
                    opacity: opacity / 100,
                    pointerEvents: "none",
                  }}
                />
              ) : (
                <span
                  style={{
                    position: "absolute",
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: "translate(-50%, -50%)",
                    opacity: opacity / 100,
                    fontFamily: '"DM Serif Display", "Fraunces", Georgia, serif',
                    color: "white",
                    fontSize: "clamp(14px, 3vw, 28px)",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                  }}
                >OM&nbsp;&nbsp;Selected</span>
              )}
              <div className="absolute top-2 left-2 eyebrow text-background bg-foreground/60 backdrop-blur px-2 py-1 text-[10px]">
                Vorschau · klicken/ziehen zum Positionieren
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div>
              <div className="eyebrow text-muted-foreground mb-2">Wasserzeichen-Bild</div>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="file"
                  accept="image/png,image/webp,image/jpeg,image/svg+xml"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                    e.target.value = "";
                  }}
                  disabled={uploading}
                  className="text-sm"
                />
                {url && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="eyebrow border border-destructive text-destructive px-3 py-1.5 text-xs"
                  >Entfernen</button>
                )}
              </div>
              {uploading && <div className="text-xs text-muted-foreground mt-2">Hochladen …</div>}
              {url && <div className="text-xs text-muted-foreground mt-2 truncate">Aktuell: {url}</div>}
            </div>

            <Slider label={`Horizontal (X): ${Math.round(x)}%`} min={0} max={100} value={x} onChange={setX} />
            <Slider label={`Vertikal (Y): ${Math.round(y)}%`} min={0} max={100} value={y} onChange={setY} />
            <Slider label={`Größe: ${Math.round(scale)}% der Bildbreite`} min={5} max={80} value={scale} onChange={setScale} />
            <Slider label={`Deckkraft: ${Math.round(opacity)}%`} min={10} max={100} value={opacity} onChange={setOpacity} />

            <div className="flex flex-wrap items-center gap-3 mt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="eyebrow border border-foreground bg-foreground text-background px-4 py-2 disabled:opacity-50"
              >{saving ? "Speichert …" : "Speichern"}</button>
              <button
                type="button"
                onClick={() => { setX(50); setY(92); setScale(25); setOpacity(94); }}
                className="eyebrow border border-border text-muted-foreground px-4 py-2"
              >Position zurücksetzen</button>
              {msg && <div className="text-xs text-muted-foreground">{msg}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Slider({ label, min, max, value, onChange }: {
  label: string;
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <div className="eyebrow text-muted-foreground mb-2 text-xs">{label}</div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
      />
    </label>
  );
}